import React, { useState, type ReactNode } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import type { ToastKind } from '../../components/Toast';
import { Button } from '../../components/ui/Button';
import { Dialog } from '../../components/ui/Dialog';
import { Switch } from '../../components/ui/Switch';
import { GoogleMark, MicrosoftMark } from '../../components/ui/brandIcons';
import { useCalendar } from '../calendar/useCalendar';
import { useOutlook } from '../calendar/useOutlook';
import { useSummaryEmail } from '../email/useSummaryEmail';
import { useAsyncData } from '../directory/useAsyncData';
import { useLanguage } from '../../lib/i18n';
import { IS_WEB } from '../../lib/platform';
import type { MeetingPrefs } from '../../global';

/** Microsoft istemcisi tanımlı değil — masaüstünde cihazın yapılandırması, web'de sunucu. */
export function microsoftNotConfigured(t: (tr: string, en: string) => string): string {
  return IS_WEB
    ? t('Microsoft bağlantısı sunucuda henüz açık değil.', 'The Microsoft connection isn’t enabled on the server yet.')
    : t('Bu bilgisayarda Microsoft bağlantısı henüz açık değil.', 'The Microsoft connection isn’t enabled on this computer yet.');
}
import { usePreferences } from './usePreferences';
import { CalendarPickerDialog } from './CalendarPickerDialog';
import { ListCard, SectionTitle } from './settingsParts';

interface CalendarSectionProps {
  onNotify: (kind: ToastKind, text: string) => void;
}

interface SwitchRowProps {
  title: string;
  description: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  /** Açıklamanın altında soluk ek bilgi. */
  note?: string;
  /** Açıklamanın altında uyarı (izin eksik vb.). */
  warning?: ReactNode;
}

const SwitchRow: React.FC<SwitchRowProps> = ({ title, description, checked, onChange, disabled, note, warning }) => (
  <div className="flex items-start justify-between gap-6 px-5 py-4">
    <div className="min-w-0 space-y-1">
      <p className="text-sm font-medium leading-none">{title}</p>
      <p className="text-[0.8rem] leading-relaxed text-muted-foreground">{description}</p>
      {note && <p className="text-[0.8rem] leading-relaxed text-muted-foreground/80">{note}</p>}
      {warning}
    </div>
    <Switch checked={checked} onChange={onChange} disabled={disabled} label={title} />
  </div>
);

/** Bağlı hesap satırı: marka, ad, hesap bilgisi ve sağda düğmeler. */
const AccountRow: React.FC<{ icon: ReactNode; name: string; detail: ReactNode; actions: ReactNode }> = ({
  icon,
  name,
  detail,
  actions,
}) => (
  <div className="flex flex-wrap items-center gap-4 px-5 py-4">
    <span className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-border bg-field">
      {icon}
    </span>
    <div className="min-w-0 flex-1">
      <p className="font-medium leading-tight">{name}</p>
      <div className="mt-0.5 truncate text-[0.8rem] text-muted-foreground">{detail}</div>
    </div>
    <div className="flex shrink-0 items-center gap-2">{actions}</div>
  </div>
);

type DisconnectTarget = 'google' | 'outlook';

/**
 * Ayarlar > Takvim: bağlı hesaplar, toplantı davranışları ve özetin
 * e-postayla paylaşılması. Entegrasyonlar ekranı da aynı ayarları yazıyor;
 * ikisi aynı değeri gösterir.
 */
export const CalendarSection: React.FC<CalendarSectionProps> = ({ onNotify }) => {
  const { t } = useLanguage();
  const { prefs, isLoaded, savePrefs } = usePreferences();

  // — Hesaplar —
  const calendar = useCalendar();
  // 'idle': durum henüz sorulmadı. 'error': hesap bağlı ama etkinlikler okunamadı.
  const isGoogleUnknown = calendar.status === 'idle';
  const isGoogleConnected =
    calendar.status === 'ready' || calendar.status === 'loading' || calendar.status === 'error';
  const isGoogleExpired = calendar.status === 'auth_expired';
  const isGoogleConnecting = calendar.status === 'connecting';
  const outlook = useOutlook(onNotify);

  const [calendarsRevision, setCalendarsRevision] = useState(0);
  const googleCalendars = useAsyncData(
    isGoogleConnected ? () => window.zabit.listGoogleCalendars() : null,
    `${isGoogleConnected}-${calendarsRevision}`
  );
  const syncingCount = googleCalendars.data?.filter((item) => item.selected).length;

  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState<DisconnectTarget | null>(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const confirmDisconnect = async () => {
    if (!disconnectTarget) return;
    setIsDisconnecting(true);
    try {
      if (disconnectTarget === 'google') await calendar.disconnect();
      else await outlook.disconnect();
      onNotify(
        'info',
        disconnectTarget === 'google'
          ? t('Google Takvim bağlantısı kesildi.', 'Google Calendar disconnected.')
          : t('Outlook bağlantısı kesildi.', 'Outlook disconnected.')
      );
    } catch (err) {
      onNotify('error', (err as Error).message);
    } finally {
      setIsDisconnecting(false);
      setDisconnectTarget(null);
    }
  };

  // — Toplantılar —
  const [autoJoinRevision, setAutoJoinRevision] = useState(0);
  const autoJoin = useAsyncData(() => window.zabit.getAutoJoinPrefs(), autoJoinRevision);
  const toggleAutoJoin = async (enabled: boolean) => {
    try {
      await window.zabit.setAutoJoinEnabled(enabled);
    } catch (err) {
      onNotify('error', (err as Error).message);
    } finally {
      setAutoJoinRevision((value) => value + 1);
    }
  };

  const [busyMeetingKey, setBusyMeetingKey] = useState<keyof MeetingPrefs | null>(null);
  const toggleMeeting = async (key: keyof MeetingPrefs, next: boolean) => {
    setBusyMeetingKey(key);
    try {
      await savePrefs({ meetings: { ...prefs.meetings, [key]: next } });
    } catch (err) {
      onNotify('error', (err as Error).message || t('Ayar kaydedilemedi.', 'Could not save the setting.'));
    } finally {
      setBusyMeetingKey(null);
    }
  };

  // — Özeti e-postayla paylaş —
  const email = useSummaryEmail(true);
  const refreshGoogleAccess = () => void calendar.connect().then(() => email.reload());
  const saveEmail = async (patch: { summaryEmailEnabled?: boolean; summaryEmailEveryone?: boolean }) => {
    try {
      await email.savePrefs(patch);
    } catch (err) {
      onNotify('error', (err as Error).message || t('Ayar kaydedilemedi.', 'Could not save the setting.'));
    }
  };

  const outlookConnected = Boolean(outlook.data?.connected);
  const outlookConfigured = Boolean(outlook.data?.configured);
  const canConnectGoogle = !isGoogleUnknown && !isGoogleConnected && !isGoogleExpired;
  const showConnectRow = canConnectGoogle || (outlook.data !== null && !outlookConnected);

  return (
    <div className="space-y-8">
      {/* — Hesaplar — */}
      <section className="space-y-2.5">
        <SectionTitle>{t('Hesaplar', 'Accounts')}</SectionTitle>
        <ListCard>
          {isGoogleUnknown && outlook.data === null && (
            <div className="px-5 py-4">
              <div className="h-10 animate-pulse rounded-lg bg-muted/60" />
            </div>
          )}

          {isGoogleConnected && (
            <AccountRow
              icon={<GoogleMark className="size-5" />}
              name={t('Google Takvim', 'Google Calendar')}
              detail={
                <>
                  {calendar.profile?.email ?? t('Bağlı', 'Connected')}
                  {syncingCount !== undefined && (
                    <>
                      {' · '}
                      {t(
                        `${syncingCount} takvim senkronize ediliyor`,
                        syncingCount === 1 ? '1 calendar syncing' : `${syncingCount} calendars syncing`
                      )}
                    </>
                  )}
                </>
              }
              actions={
                <>
                  <Button variant="secondary" size="sm" onClick={() => setIsPickerOpen(true)}>
                    {t('Yönet', 'Manage')}
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => setDisconnectTarget('google')}>
                    {t('Bağlantıyı kes', 'Disconnect')}
                  </Button>
                </>
              }
            />
          )}

          {isGoogleExpired && (
            <AccountRow
              icon={<GoogleMark className="size-5" />}
              name={t('Google Takvim', 'Google Calendar')}
              detail={<span className="text-amber-500">{t('Yetki süresi doldu', 'Access expired')}</span>}
              actions={
                <Button size="sm" disabled={isGoogleConnecting} onClick={() => void calendar.connect()}>
                  {isGoogleConnecting && <Loader2 className="size-4 animate-spin" />}
                  {t('Yeniden bağla', 'Reconnect')}
                </Button>
              }
            />
          )}

          {outlookConnected && (
            <AccountRow
              icon={<MicrosoftMark className="size-5" />}
              name={t('Outlook Takvimi', 'Outlook Calendar')}
              detail={
                <>
                  {outlook.data?.profile?.email ?? t('Bağlı', 'Connected')}
                  {' · '}
                  {t('varsayılan takvim', 'default calendar')}
                </>
              }
              actions={
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={outlook.isBusy}
                  onClick={() => setDisconnectTarget('outlook')}
                >
                  {t('Bağlantıyı kes', 'Disconnect')}
                </Button>
              }
            />
          )}

          {showConnectRow && (
            <div className="flex flex-wrap gap-2 px-5 py-4">
              {canConnectGoogle && (
                <Button variant="secondary" disabled={isGoogleConnecting} onClick={() => void calendar.connect()}>
                  {isGoogleConnecting ? <Loader2 className="size-4 animate-spin" /> : <GoogleMark />}
                  {t('Google Takvim’i bağla', 'Connect Google Calendar')}
                </Button>
              )}
              {outlook.data !== null && !outlookConnected && (
                <Button
                  variant="secondary"
                  disabled={outlook.isBusy || !outlookConfigured}
                  title={outlookConfigured ? undefined : microsoftNotConfigured(t)}
                  onClick={() => void outlook.connect()}
                >
                  {outlook.isBusy ? <Loader2 className="size-4 animate-spin" /> : <MicrosoftMark />}
                  {outlook.data.expired ? t('Outlook’u yeniden bağla', 'Reconnect Outlook') : t('Outlook’u bağla', 'Connect Outlook')}
                </Button>
              )}
            </div>
          )}

          {calendar.connectError && (
            <p className="px-5 py-3 text-[0.8rem] font-medium text-destructive">{calendar.connectError}</p>
          )}

          {outlook.data !== null && !outlookConnected && (
            <p className="px-5 py-4 text-[0.8rem] leading-relaxed text-muted-foreground">
              {t(
                'Microsoft kendi giriş sayfasını açar — son onay adımına kadar TalkNote’tan bahsetmez. Giriş yap, sonra takvim erişimine izin ver; TalkNote etkinliklerini yalnızca toplantı notlarını hazırlamak için okur.',
                'Microsoft opens its own sign-in page — it won’t mention TalkNote until the final consent step. Sign in, then allow calendar access; TalkNote only reads your events to prepare meeting notes.'
              )}
              {!outlookConfigured && <> {microsoftNotConfigured(t)}</>}
            </p>
          )}
        </ListCard>
      </section>

      {/* — Toplantılar — */}
      <section className="space-y-2.5">
        <SectionTitle>{t('Toplantılar', 'Meetings')}</SectionTitle>
        <ListCard>
          <SwitchRow
            title={t('Toplantılarıma not alıcı gönder', 'Send a notetaker to my meetings')}
            description={t(
              'TalkNote botu, takvimindeki Meet, Zoom ve Teams toplantılarına katılıp kaydeder — sen orada olmasan bile. Reddettiğin toplantılara gitmez; istemediğin toplantıyı ana sayfada kendi düğmesinden kapatabilirsin.',
              'The TalkNote bot joins and records the Meet, Zoom and Teams meetings on your calendar — even if you’re not there. It skips meetings you declined; switch off any other meeting with its own switch on the home page.'
            )}
            checked={autoJoin.data?.autoJoinBot ?? false}
            disabled={!autoJoin.data}
            onChange={(next) => void toggleAutoJoin(next)}
          />
          {/* Yüzen hatırlatıcı ve görüşme algılama masaüstü uygulamasına özgü (web'de pencere yok). */}
          {!IS_WEB && (
            <SwitchRow
              title={t('Takvimimi izle', 'Watch my calendar for meetings')}
              description={t(
                'Her toplantıdan yaklaşık 1 dakika önce köşede küçük bir hatırlatıcı çıkar; tek tıkla kaydı başlatırsın.',
                'Shows a small floating reminder about 1 minute before each meeting, with one tap to record.'
              )}
              checked={prefs.meetings.calendarReminders}
              disabled={!isLoaded || busyMeetingKey === 'calendarReminders'}
              onChange={(next) => void toggleMeeting('calendarReminders', next)}
            />
          )}
          {!IS_WEB && (
            <SwitchRow
              title={t('Görüşmedeyken algıla', 'Detect when I’m in a call')}
              description={t(
                'Bir görüşmeye katıldığın anda tek tıklık bir “Kaydet” uyarısı çıkar — takvimde olmayan anlık görüşmelerde bile.',
                'Pops a one-tap “Record” prompt the moment you join a call — even ad-hoc meetings that aren’t on your calendar.'
              )}
              note={
                isLoaded && !prefs.callDetectionSupported
                  ? t('Şimdilik yalnızca Windows’ta çalışıyor.', 'Only available on Windows for now.')
                  : undefined
              }
              checked={prefs.callDetectionSupported && prefs.meetings.detectCalls}
              disabled={!isLoaded || !prefs.callDetectionSupported || busyMeetingKey === 'detectCalls'}
              onChange={(next) => void toggleMeeting('detectCalls', next)}
            />
          )}
          <SwitchRow
            title={t('Görüşme bitince kaydı durdur', 'Stop recording when the call ends')}
            description={t(
              'Kaydettiğin görüşme bitince TalkNote kısa bir geri sayımdan sonra kaydı durdurur; kayıt yanlışlıkla açık kalmaz. Tek tıkla kayda devam edebilirsin.',
              'When the call you’re recording ends, TalkNote stops after a short countdown so you never leave a recording running by accident. You can keep recording with one tap.'
            )}
            note={
              isLoaded && !prefs.meetings.autoStopOnCallEnd
                ? t('Kapalıyken yalnızca 4 saati geçen kayıtlar için sorulur.', 'While off, you’re only asked once a recording passes 4 hours.')
                : undefined
            }
            checked={prefs.meetings.autoStopOnCallEnd}
            disabled={!isLoaded || busyMeetingKey === 'autoStopOnCallEnd'}
            onChange={(next) => void toggleMeeting('autoStopOnCallEnd', next)}
          />
        </ListCard>
      </section>

      {/* — Özeti e-postayla paylaş — */}
      <section className="space-y-2.5">
        <SectionTitle>{t('Özeti e-postayla paylaş', 'Share summary as email')}</SectionTitle>
        <ListCard>
          <SwitchRow
            title={t('Kendime', 'Myself')}
            description={
              email.status.enabled && email.status.recipient
                ? t(
                    `Toplantı notları gelen kutuna gelsin (${email.status.recipient}).`,
                    `Get meeting notes in your inbox (${email.status.recipient}).`
                  )
                : t('Toplantı notları gelen kutuna gelsin.', 'Get meeting notes in your inbox.')
            }
            checked={email.status.enabled}
            disabled={email.isLoading}
            onChange={(next) => void saveEmail({ summaryEmailEnabled: next })}
          />
          <SwitchRow
            title={t('Herkese', 'Everyone')}
            description={t('Notları toplantıdaki herkesle paylaş.', 'Share notes with everyone in the meeting.')}
            note={t(
              'Takvimden gelen katılımcı adreslerine TalkNote’un adresinden tek bir e-posta gider; yanıtlar sana döner. Takvimde olmayan kayıtlarda yalnızca sana gider.',
              'One email goes from TalkNote’s address to the participant addresses from your calendar; replies come back to you. Recordings that aren’t on your calendar only go to you.'
            )}
            checked={email.status.everyone}
            disabled={email.isLoading}
            onChange={(next) => void saveEmail({ summaryEmailEveryone: next })}
          />
        </ListCard>
      </section>

      <CalendarPickerDialog
        open={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        onSaved={() => {
          setCalendarsRevision((value) => value + 1);
          void calendar.refresh();
        }}
        onNotify={onNotify}
      />

      <Dialog
        open={disconnectTarget !== null}
        onClose={() => {
          if (!isDisconnecting) setDisconnectTarget(null);
        }}
        title={
          disconnectTarget === 'outlook'
            ? t('Outlook bağlantısı kesilsin mi?', 'Disconnect Outlook?')
            : t('Google Takvim bağlantısı kesilsin mi?', 'Disconnect Google Calendar?')
        }
        description={
          disconnectTarget === 'outlook'
            ? t(
                'Outlook toplantıları listenden ve otomatik bottan çıkar. İstediğin zaman yeniden bağlayabilirsin.',
                'Outlook meetings leave your list and the notetaker. You can reconnect any time.'
              )
            : t(
                'Toplantı listesi, otomatik bot, hatırlatıcılar ve özet e-postası çalışmayı durdurur. İstediğin zaman yeniden bağlayabilirsin.',
                'Your meeting list, the notetaker, reminders and summary emails stop working. You can reconnect any time.'
              )
        }
        footer={
          <>
            <Button variant="secondary" disabled={isDisconnecting} onClick={() => setDisconnectTarget(null)}>
              {t('Vazgeç', 'Cancel')}
            </Button>
            <Button variant="destructive" disabled={isDisconnecting} onClick={() => void confirmDisconnect()}>
              {isDisconnecting && <Loader2 className="size-4 animate-spin" />}
              {t('Bağlantıyı kes', 'Disconnect')}
            </Button>
          </>
        }
      >
        <span className="sr-only">
          {disconnectTarget === 'outlook' ? outlook.data?.profile?.email : calendar.profile?.email}
        </span>
      </Dialog>
    </div>
  );
};
